import React, { useEffect, useState, useCallback } from "react";
import {
    Box,
    Typography,
    CircularProgress,
    Paper,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    IconButton,
    Tooltip,
    Link
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { DataGrid } from "@mui/x-data-grid";
import Layout from "../../components/Layout";
import SnackbarNotification from "../../components/SnackbarNotification";
import LinkedInAccountGuide from "./LinkedInAccountGuide"; // Import the new guide component
import config from "../../Config";
import * as Sentry from "@sentry/react";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { isLoggedIn } from "../../utils/utils";
import styles from "./AccountsPage.styles";

const AccountsPage = () => {
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [cookiesText, setCookiesText] = useState("");
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
    const [accountToDelete, setAccountToDelete] = useState(null);
    const [guideOpen, setGuideOpen] = useState(false); // State for the guide modal

    const navigate = useNavigate();

    const fetchAccounts = useCallback(async () => {
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user?.token) return;

        setLoading(true);
        try {
            const response = await fetch(`${config.apiBaseUrl}/account`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "user-authorization": user.token,
                },
            });

            if (!response.ok) {
                setSnackbar({
                    open: true,
                    message: `Failed to fetch accounts: Unknown error`,
                    severity: "error",
                });
                return;
            }

            const data = await response.json();
            const formattedAccounts = data.map((account, index) => ({
                ...account,
                id: account.id || `temp-id-${index}`,
                created_at: account.created_at ? dayjs(account.created_at).format("YYYY-MM-DD HH:mm:ss") : "N/A",
                modified_at: account.modified_at ? dayjs(account.modified_at).format("YYYY-MM-DD HH:mm:ss") : "N/A",
                name: account.name || "N/A",
                url: account.url || "",
                headline: account.headline || "N/A",
            }));

            setAccounts(formattedAccounts);
        } catch (error) {
            Sentry.captureException(error);
            setSnackbar({ open: true, message: "Error fetching accounts.", severity: "error" });
            console.error("Error fetching accounts:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!isLoggedIn()) {
            navigate("/login");
            return;
        }
        (async () => {
            await fetchAccounts();
        })();
    }, [fetchAccounts, navigate]);

    const handleAddAccount = useCallback(async () => {
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user?.token) return;

        try {
            const cookies = JSON.parse(cookiesText);

            const response = await fetch(`${config.apiBaseUrl}/account`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "user-authorization": user.token,
                },
                body: JSON.stringify({ cookies }),
            });

            if (response.status === 200) {
                setSnackbar({ open: true, message: "Account added successfully!", severity: "success" });
                setOpenDialog(false);
                setCookiesText("");
                await fetchAccounts();
            } else {

                const errorData = await response.json();
                const errorMessage = errorData.detail || "Failed to add account";

                setSnackbar({
                    open: true,
                    message: errorMessage,
                    severity: "error",
                });
            }
        } catch (error) {
            Sentry.captureException(error);
            setSnackbar({ open: true, message: "Error adding account. Please check the cookies.", severity: "error" });
            console.error("Error adding account:", error);
        }
    }, [cookiesText, fetchAccounts]);

    const handleDeleteAccount = useCallback(async () => {
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user?.token || !accountToDelete) return;

        try {
            const response = await fetch(`${config.apiBaseUrl}/account?account_id=${accountToDelete}`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    "user-authorization": user.token,
                },
            });

            if (response.ok) {
                setSnackbar({ open: true, message: "Account deleted successfully!", severity: "success" });
                await fetchAccounts();
            } else {
                const errorData = await response.json();
                const errorMessage = errorData.detail || "Failed to delete account";

                setSnackbar({
                    open: true,
                    message: errorMessage,
                    severity: "error",
                });
            }
        } catch (error) {
            Sentry.captureException(error);
            setSnackbar({ open: true, message: "Error deleting account.", severity: "error" });
            console.error("Error deleting account:", error);
        } finally {
            setDeleteConfirmDialog(false);
            setAccountToDelete(null);
        }
    }, [accountToDelete, fetchAccounts]);

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setCookiesText("");
    };

    // Toggle guide modal
    const toggleGuide = () => {
        setGuideOpen(!guideOpen);
    };

    const columns = [
        { field: "id", headerName: "Account ID", width: 300 },
        { field: "name", headerName: "Name", width: 200 },
        {
            field: "url",
            headerName: "Profile URL",
            width: 250,
            renderCell: (params) => {
                if (params.value && params.value.trim() !== "") {
                    return (
                        <a href={params.value} target="_blank" rel="noopener noreferrer" style={{ color: "#0073b1", textDecoration: "none" }}>
                            LinkedIn Profile
                        </a>
                    );
                }
                return "N/A";
            }
        },
        { field: "headline", headerName: "Headline", width: 300 },
        { field: "created_at", headerName: "Created At", width: 200 },
        { field: "modified_at", headerName: "Modified At", width: 200 },
        {
            field: "actions",
            headerName: "Actions",
            width: 120,
            sortable: false,
            renderCell: (params) => (
                <Tooltip title="Delete Account">
                    <IconButton
                        color="error"
                        onClick={() => {
                            setAccountToDelete(params.row.id);
                            setDeleteConfirmDialog(true);
                        }}
                    >
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            )
        }
    ];

    return (
        <Layout>
            {loading ? (
                <Box sx={styles.loadingContainer}>
                    <CircularProgress />
                </Box>
            ) : (
                <Box sx={{ width: "100%", px: 2, mt: 0 }}>
                    <Box sx={styles.pageHeader}>
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>Accounts</Typography>
                        <Typography variant="body1" sx={{ color: "rgba(0, 0, 0, 0.6)" }}>Home • Accounts</Typography>
                    </Box>

                    <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => setOpenDialog(true)}
                            >
                                Add Account
                            </Button>

                            {/* Help link to open guide */}
                            <Link
                                component="button"
                                variant="body2"
                                onClick={toggleGuide}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: 'primary.main',
                                    textDecoration: 'none',
                                    '&:hover': { textDecoration: 'underline' }
                                }}
                            >
                                <HelpOutlineIcon fontSize="small" sx={{ mr: 0.5 }} />
                                How to add LinkedIn account?
                            </Link>
                        </Box>
                    </Box>

                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>Accounts List</Typography>

                    <Paper elevation={3} sx={styles.dataGridPaper}>
                        <DataGrid
                            rows={accounts}
                            columns={columns}
                            initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
                            pageSizeOptions={[5, 10, 25]}
                            disableRowSelectionOnClick
                        />
                    </Paper>

                    {/* Add Account Dialog */}
                    <Dialog open={openDialog} onClose={handleCloseDialog}>
                        <DialogTitle>ADD NEW ACCOUNT</DialogTitle>
                        <DialogContent>
                            <Typography variant="body2" sx={{ color: "red", mb: 2 }}>
                                The cookies should be in the following format (exported from Cookie-Editor)
                            </Typography>
                            <TextField
                                label="Cookies JSON"
                                value={cookiesText}
                                onChange={(e) => setCookiesText(e.target.value)}
                                variant="outlined"
                                fullWidth
                                multiline
                                rows={6}
                                sx={styles.dialogTextField}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleCloseDialog} color="primary">Close</Button>
                            <Button onClick={handleAddAccount} color="primary">Save</Button>
                        </DialogActions>
                    </Dialog>

                    {/* Delete Confirmation Dialog */}
                    <Dialog open={deleteConfirmDialog} onClose={() => setDeleteConfirmDialog(false)}>
                        <DialogTitle>Delete Account</DialogTitle>
                        <DialogContent>
                            <Typography variant="body1">
                                Are you sure you want to delete this account? This action cannot be undone.
                            </Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setDeleteConfirmDialog(false)} color="primary">Cancel</Button>
                            <Button onClick={handleDeleteAccount} color="error">Delete</Button>
                        </DialogActions>
                    </Dialog>

                    {/* LinkedIn Account Guide Modal */}
                    <LinkedInAccountGuide
                        open={guideOpen}
                        onClose={() => setGuideOpen(false)}
                    />

                    <SnackbarNotification
                        open={snackbar.open}
                        message={snackbar.message}
                        severity={snackbar.severity}
                        onClose={() => setSnackbar({ ...snackbar, open: false })}
                    />
                </Box>
            )}
        </Layout>
    );
};

export default AccountsPage;